import { tw, apply } from 'twind/css';
import { useQuery } from 'urql';

import { VariantProduct } from '__generated__/graphql';
import { RecommendationTile } from 'ui/components/RecommendationTile';
import { LoadingSpinner } from 'ui/components/LoadingSpinner';
import { isNotNullish } from 'utils/isNotNullish';
import { useIsCrawler } from 'hooks/useIsCrawler';
import { useIsServer } from 'hooks/useIsServer';
import { ManagedHeading } from 'ui/components/ManagedHeading';
import { RECOMMENDATIONS_QUERY } from 'gql/queries/recommendations';

const MAX_RECOMMENDATIONS = 4;

export const ProductRecommendations = ({
  recommender,
  masterProduct,
  content,
}) => {
  const isCrawler = useIsCrawler();

  const recommenderType = content?.soldOutRecommender?.[0]?.type;
  const isServer = useIsServer();
  const [result] = useQuery({
    query: RECOMMENDATIONS_QUERY,
    variables: {
      pageType: recommenderType,
      ids: [masterProduct.id, masterProduct?.primaryCategoryId].filter(
        isNotNullish
      ),
    },
    pause: (isServer && !isCrawler) || !recommenderType,
  });

  if (!recommender) return null;

  const productRecommender = recommender[0];
  const recoType = productRecommender?.__typename;

  if (recoType === 'ProductRecommender' && !recommenderType) return null;
  let products: VariantProduct[] = [];

  const recommendations =
    result.data?.recommendationsWithDetails?.recommendations;

  if (recoType === 'ProductRecommender') {
    if (recommendations) {
      products = (recommendations || []).reduce((acc, curr) => {
        if (curr?.variantProduct) {
          return [...acc, curr.variantProduct] as VariantProduct[];
        }
        return acc;
      }, [] as VariantProduct[]);
    }
  } else if (recoType === 'ProductCarousel') {
    products = productRecommender?.products;
  }

  if (result.fetching) {
    return <LoadingSpinner className="w-40 h-40 my-40 mx-auto" />;
  }

  return (
    <section>
      <hr />
      {productRecommender?.header && (
        <ManagedHeading
          tag="h1"
          dataTestId="recommender-header"
          className={tw([
            apply('uppercase font-bold text-xl mobile:text-lg mb-1 pt-6'),
            !productRecommender?.copy && 'mb-6',
          ])}
        >
          {productRecommender?.header}
        </ManagedHeading>
      )}
      {productRecommender?.copy && (
        <h3 className="text-base mb-6">{productRecommender?.copy}</h3>
      )}
      <div
        className={tw(
          'grid gap-x-4 gap-y-8 transition-opacity duration-700',
          'grid-cols-2'
        )}
      >
        {products.slice(0, MAX_RECOMMENDATIONS).map(product => (
          <RecommendationTile
            key={product.id}
            containerClassName="h-full justify-between"
            dataTestId="carousel-product-item"
            linkClassName="text-center text-xs"
            product={product}
            productInfoConfig={{
              priceContainerClassName: 'hidden',
            }}
          />
        ))}
      </div>
    </section>
  );
};
